import StyledBooleanCheckbox from '@/components/styled/StyledBooleanCheckbox';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  getHotelRoomCombinations,
  HotelRoomType,
} from '@/constants/HotelRoomTypes';
import { useEffect, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  get,
  useFormContext,
  UseFormSetValue,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from '@/i18n';
import { HotelFormDataWithTranslations } from '@/i18n/translation-type-utils';
import {
  hasUncheckedRoomTypePartitions,
  pruneUncheckedRoomTypePartitions,
} from '@/utils/benefits/hotel/pruneUncheckedRoomTypePartitions';

interface RoomCategoryTypesProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  isViewMode?: boolean;
}

const RoomCategoryTypes = ({
  control,
  index,
  isViewMode = false,
}: RoomCategoryTypesProps) => {
  const { t } = useTranslation();

  const childAges = useWatch({ control, name: 'childAges' });
  const acceptsChildPricing = useWatch({
    control,
    name: 'acceptsChildPricing',
  });
  const roomTypes = useWatch({
    control,
    name: `hotelRoomCategories.${index}.roomTypes`,
  });

  const numberOfChildrenRepartitions = useMemo(
    () => (acceptsChildPricing === 'true' ? (childAges?.length ?? 0) : 0),
    [acceptsChildPricing, childAges?.length],
  );

  const { setValue } = useFormContext();
  const { getValues } = useFormContext<HotelFormDataWithTranslations>();
  const tarifsCount =
    useWatch({ control, name: `hotelRoomCategories.${index}.tarifs` })
      ?.length ?? 0;

  // The price lines of an unticked room type must go on every tarif grid of
  // the category. The grid lists only clear themselves while mounted, and a
  // collapsed (or freshly duplicated) grid is not, so it kept the stale lines.
  useEffect(() => {
    const tarifs = getValues(`hotelRoomCategories.${index}.tarifs`) ?? [];
    tarifs.forEach((tarif, tarifIndex) => {
      if (!hasUncheckedRoomTypePartitions(tarif.pricePartitions, roomTypes))
        return;
      setValue(
        `hotelRoomCategories.${index}.tarifs.${tarifIndex}.pricePartitions`,
        pruneUncheckedRoomTypePartitions(tarif.pricePartitions, roomTypes),
      );
    });
  }, [getValues, index, roomTypes, setValue, tarifsCount]);

  useEffect(() => {
    if (roomTypes.triple && numberOfChildrenRepartitions === 0) {
      setValue(`hotelRoomCategories.${index}.tripleRoomOptions`, [0]);
    }
    if (roomTypes.quadruple && numberOfChildrenRepartitions === 0) {
      setValue(`hotelRoomCategories.${index}.quadrupleRoomOptions`, [0]);
    }
  }, [
    index,
    numberOfChildrenRepartitions,
    roomTypes.quadruple,
    roomTypes.triple,
    setValue,
  ]);

  useEffect(() => {
    if (acceptsChildPricing === 'false') {
      setValue(
        `hotelRoomCategories.${index}.doubleRoomOptions`,
        roomTypes.double ? [0] : [],
      );
      setValue(
        `hotelRoomCategories.${index}.tripleRoomOptions`,
        roomTypes.triple ? [0] : [],
      );
      setValue(
        `hotelRoomCategories.${index}.quadrupleRoomOptions`,
        roomTypes.quadruple ? [0] : [],
      );
    }
  }, [
    acceptsChildPricing,
    index,
    roomTypes.double,
    roomTypes.quadruple,
    roomTypes.triple,
    setValue,
  ]);

  const { errors } = useFormState({
    control,
  });
  const errorMessage = get(
    errors,
    `hotelRoomCategories.${index}.roomTypes.root` + '.message',
  ) as unknown as string;

  return (
    <>
      <h2 className="text-base font-medium text-black">
        {t('hotels.roomCategory.roomTypes.title')}
      </h2>
      <Label className="text-gray-700">
        {t('hotels.roomCategory.roomTypes.question')}
      </Label>
      {errorMessage !== '' && (
        <p className="text-sm leading-6 font-medium text-error-500">
          {errorMessage}
        </p>
      )}
      <StyledBooleanCheckbox
        className="text-gray-700"
        control={control}
        name={`hotelRoomCategories.${index}.roomTypes.individual`}
        optionLabel={t('hotels.roomCategory.roomTypes.individual')}
        disabled={isViewMode}
      />
      {roomTypes.individual && (
        <RoomTypeOptions
          control={control}
          index={index}
          roomType={HotelRoomType.INDIVIDUAL}
          numberOfRepartitions={numberOfChildrenRepartitions}
          isViewMode={isViewMode}
          childAges={childAges}
          setValue={setValue}
        />
      )}
      <StyledBooleanCheckbox
        className="text-gray-700"
        control={control}
        name={`hotelRoomCategories.${index}.roomTypes.double`}
        optionLabel={t('hotels.roomCategory.roomTypes.double')}
        disabled={isViewMode}
      />
      {roomTypes.double && (
        <RoomTypeOptions
          control={control}
          index={index}
          roomType={HotelRoomType.DOUBLE}
          numberOfRepartitions={numberOfChildrenRepartitions}
          isViewMode={isViewMode}
          childAges={childAges}
          setValue={setValue}
        />
      )}
      <StyledBooleanCheckbox
        className="text-gray-700"
        control={control}
        name={`hotelRoomCategories.${index}.roomTypes.triple`}
        optionLabel={t('hotels.roomCategory.roomTypes.triple')}
        disabled={isViewMode}
      />
      {roomTypes.triple && (
        <RoomTypeOptions
          control={control}
          index={index}
          roomType={HotelRoomType.TRIPLE}
          numberOfRepartitions={numberOfChildrenRepartitions}
          isViewMode={isViewMode}
          childAges={childAges}
          setValue={setValue}
        />
      )}
      <StyledBooleanCheckbox
        className="text-gray-700"
        control={control}
        name={`hotelRoomCategories.${index}.roomTypes.quadruple`}
        optionLabel={t('hotels.roomCategory.roomTypes.quadruple')}
        disabled={isViewMode}
      />
      {roomTypes.quadruple && (
        <RoomTypeOptions
          control={control}
          index={index}
          roomType={HotelRoomType.QUADRUPLE}
          numberOfRepartitions={numberOfChildrenRepartitions}
          isViewMode={isViewMode}
          childAges={childAges}
          setValue={setValue}
        />
      )}
    </>
  );
};

export default RoomCategoryTypes;

interface RoomTypeOptionsProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  roomType: HotelRoomType;
  numberOfRepartitions: number;
  childAges?: {
    childAgeFrom?: number | undefined;
    childAgeTo?: number | undefined;
  }[];
  setValue: UseFormSetValue<FieldValues>;
  isViewMode?: boolean;
}

const RoomTypeOptions = ({
  control,
  index,
  roomType,
  numberOfRepartitions,
  childAges = [
    { childAgeFrom: 2, childAgeTo: 11 },
    { childAgeFrom: 12, childAgeTo: 17 },
  ],
  setValue,
  isViewMode = false,
}: RoomTypeOptionsProps) => {
  const { t } = useTranslation();

  const roomOptionName = useMemo(() => {
    switch (roomType) {
      case HotelRoomType.INDIVIDUAL:
        return 'individualRoomOptions';
      case HotelRoomType.DOUBLE:
        return 'doubleRoomOptions';
      case HotelRoomType.TRIPLE:
        return 'tripleRoomOptions';
      case HotelRoomType.QUADRUPLE:
        return 'quadrupleRoomOptions';
    }
  }, [roomType]);

  const roomRepartitions = useMemo(
    () =>
      getHotelRoomCombinations(roomType, numberOfRepartitions).map(
        ({ combination, index }) =>
          numberOfRepartitions === 2
            ? {
                combination: [combination[0], combination[2], combination[1]],
                index,
              }
            : { combination, index },
      ),
    [numberOfRepartitions, roomType],
  );

  const roomOptions = useWatch({
    control,
    name: `hotelRoomCategories.${index}.${roomOptionName}`,
  });

  useEffect(() => {
    if (
      numberOfRepartitions === 1 &&
      roomOptions.some(
        (option) =>
          !roomRepartitions
            .map((repartition) => repartition.index)
            .includes(option),
      )
    ) {
      setValue(
        `hotelRoomCategories.${index}.${roomOptionName}`,
        roomOptions.filter((option) =>
          roomRepartitions
            .map((repartition) => repartition.index)
            .includes(option),
        ),
      );
    }
  }, [
    index,
    numberOfRepartitions,
    roomOptionName,
    roomOptions,
    roomRepartitions,
    setValue,
  ]);

  return (
    <table className="w-fit">
      <thead>
        <tr>
          <th className="text-gray-700 p-2">
            <Label>{t('hotels.adult')}</Label>
          </th>
          {numberOfRepartitions > 1 && (
            <th className="text-gray-700 p-2">
              <span>
                <Label>
                  {t('hotels.childAge', {
                    ageFrom: childAges[1].childAgeFrom,
                    ageTo: childAges[1].childAgeTo,
                  })}
                </Label>
              </span>
            </th>
          )}
          {numberOfRepartitions > 0 && (
            <th className="text-gray-700 p-2">
              <Label>
                {t('hotels.childAge', {
                  ageFrom: childAges[0].childAgeFrom,
                  ageTo: childAges[0].childAgeTo,
                })}
              </Label>
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {roomRepartitions.map(
          ({ combination: combinations, index: repartitionIndex }) => (
            <tr key={`combinations-${roomType}-${repartitionIndex}`}>
              {combinations.map((roomCapacity, indexCombination) => (
                <td
                  key={`combination-${roomType}-${repartitionIndex}-${indexCombination}`}
                  className="p-2"
                >
                  <div className="text-center size-10 p-2 bg-white rounded-lg mx-auto">
                    {roomCapacity}
                  </div>
                </td>
              ))}
              <RoomTypeCheckbox
                control={control}
                index={index}
                roomType={roomType}
                repartitionIndex={repartitionIndex}
                isViewMode={isViewMode}
              />
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
};

interface RoomTypeCheckboxProps {
  control: Control<HotelFormDataWithTranslations>;
  index: number;
  roomType: HotelRoomType;
  repartitionIndex: number;
  isViewMode?: boolean;
}

const RoomTypeCheckbox = ({
  control,
  index,
  roomType,
  repartitionIndex,
  isViewMode = false,
}: RoomTypeCheckboxProps) => {
  const roomOptionName = useMemo(() => {
    switch (roomType) {
      case HotelRoomType.INDIVIDUAL:
        return 'individualRoomOptions';
      case HotelRoomType.DOUBLE:
        return 'doubleRoomOptions';
      case HotelRoomType.TRIPLE:
        return 'tripleRoomOptions';
      case HotelRoomType.QUADRUPLE:
        return 'quadrupleRoomOptions';
    }
  }, [roomType]);
  const { errors } = useFormState({
    control,
  });
  const errorMessage = get(
    errors,
    `hotelRoomCategories.${index}.${roomOptionName}` + '.message',
  );
  return (
    <>
      <td className="p-2">
        <Controller
          control={control}
          name={`hotelRoomCategories.${index}.${roomOptionName}`}
          render={({ field }) => {
            field.value = field.value ?? [];
            return (
              <Checkbox
                {...field}
                checked={field.value.includes(repartitionIndex)}
                onClick={() =>
                  field.onChange(
                    field.value.includes(repartitionIndex)
                      ? field.value.filter(
                          (value) => value !== repartitionIndex,
                        )
                      : [...field.value, repartitionIndex].sort(),
                  )
                }
                value={repartitionIndex}
                disabled={isViewMode}
              />
            );
          }}
        />
        {errorMessage !== '' && (
          <p className="text-sm leading-6 font-medium text-error-500">
            {errorMessage}
          </p>
        )}
      </td>
    </>
  );
};
